import { useEffect, useRef } from 'react';
import type { FaceDetector, ObjectDetector } from '@mediapipe/tasks-vision';
import { IntegrityEventType } from 'generated/graphql';
import {
  CloseIntegrityEvent,
  OpenIntegrityEvent,
  ReportIntegrityEvent,
} from './useIntegrityMonitoring';

// Pinned to the installed @mediapipe/tasks-vision version so the CDN wasm
// always matches the bundled JS API.
const TASKS_VISION_VERSION = '0.10.35';
const WASM_BASE_URL = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${TASKS_VISION_VERSION}/wasm`;
const FACE_MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite';
// Lite2 over lite0: noticeably better phone/not-phone separation in
// standalone webcam testing, at ~3x the inference cost.
const OBJECT_MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/object_detector/efficientdet_lite2/float16/1/efficientdet_lite2.tflite';

const SAMPLE_INTERVAL_MS = 500;
// The object detector runs on every sample despite being much heavier than
// BlazeFace: with the consecutive-sample hysteresis below, sampling faster
// is what keeps phone-detection latency low.
const PHONE_SAMPLE_EVERY_N_TICKS = 1;
const MIN_FACE_DETECTION_CONFIDENCE = 0.5;
// Phone scoring is a Schmitt trigger, not a single cutoff: a real phone's
// per-frame score fluctuates (angle, motion blur, the hand covering part of
// it), so one strict threshold applied to every frame either misses real
// phones or — set low — lets steady mid-score objects (mugs, remotes) through.
// A streak must ENTER on a confidently-scored frame, then merely SUSTAIN
// above a lower bar; false positives rarely produce the confident entry
// frame, while real phones only need to peak once.
const PHONE_ENTER_SCORE = 0.65;
const PHONE_SUSTAIN_SCORE = 0.5;
// Detector-level floor: below the sustain bar a detection is never relevant,
// so let the detector drop it before it reaches scoring.
const PHONE_DETECTOR_MIN_SCORE = PHONE_SUSTAIN_SCORE;
const PHONE_MAX_RESULTS = 6;
// Consecutive-sample hysteresis so a single blurry frame (turning head,
// hand over lens) doesn't open or close an event.
const OPEN_AFTER_POSITIVE_SAMPLES = 3;
const CLOSE_AFTER_NEGATIVE_SAMPLES = 2;
const MIN_EVENT_DURATION_SECONDS = 1;
// Brief look-aways (sipping water, glancing down) are normal; only report
// the face missing when it stays out of frame this long.
const MIN_FACE_ABSENCE_SECONDS = 6;
// A persistently failing detector (lost WebGL context, OOM on a weak
// machine) is shut down instead of burning CPU every second.
const MAX_CONSECUTIVE_TICK_FAILURES = 5;

type HookProps = {
  // Vision detection only makes sense while the monitoring session is live.
  enabled: boolean;
  // The camera feed to analyse. Null pauses detection and resets streaks
  // (camera muted/off); a new stream (device switch) resumes on the new feed.
  stream: MediaStream | null;
  openEvent: OpenIntegrityEvent;
  closeEvent: CloseIntegrityEvent;
  reportEvent: ReportIntegrityEvent;
};

// Client-side camera analysis (Google MediaPipe): BlazeFace flags
// no-face/multiple-faces, EfficientDet flags visible phones. Everything runs
// in-browser against the provided stream via a detached video element —
// nothing is shown to the candidate, and failures never interrupt the
// assessment. Source-agnostic: owner hooks feed it a LiveKit-derived stream
// (AI interview) or a raw getUserMedia stream (online assessments).
export function useVisionIntegrityDetection({
  enabled,
  stream,
  openEvent,
  closeEvent,
  reportEvent,
}: HookProps): void {
  // Stream swaps are routed into the running effect through refs so a device
  // switch doesn't tear the detectors down and re-initialise MediaPipe.
  const streamRef = useRef<MediaStream | null>(stream);
  const applyStreamRef = useRef<((next: MediaStream | null) => void) | null>(
    null,
  );

  useEffect(() => {
    streamRef.current = stream;
    applyStreamRef.current?.(stream);
  }, [stream]);

  useEffect(() => {
    if (!enabled) return;

    let disposed = false;
    let paused = false;
    let tick = 0;
    // Whether the last phone sample was positive — decides which side of the
    // enter/sustain hysteresis the next sample is scored against.
    let phoneStreakActive = false;
    let consecutiveFailures = 0;
    let interval: ReturnType<typeof setInterval> | null = null;
    let faceDetector: FaceDetector | null = null;
    let objectDetector: ObjectDetector | null = null;
    let delegate: 'GPU' | 'CPU' = 'GPU';
    let createDetectorPair:
      | ((target: 'GPU' | 'CPU') => Promise<[FaceDetector, ObjectDetector]>)
      | null = null;

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;

    const noFace = makeSignalTracker(
      IntegrityEventType.NoFaceDetected,
      openEvent,
      closeEvent,
      { minDurationSeconds: MIN_FACE_ABSENCE_SECONDS },
    );
    const multipleFaces = makeSignalTracker(
      IntegrityEventType.MultipleFacesDetected,
      openEvent,
      closeEvent,
    );
    // Default hysteresis: a phone must persist across consecutive samples
    // before an event opens, so a single spurious high-confidence frame
    // doesn't report a candidate.
    const phone = makeSignalTracker(
      IntegrityEventType.PhoneDetected,
      openEvent,
      closeEvent,
    );

    const resetSignals = () => {
      noFace.reset();
      multipleFaces.reset();
      phone.reset();
      phoneStreakActive = false;
    };

    const applyStream = (next: MediaStream | null) => {
      // Streaks built on the old feed must not carry over to the new one;
      // when the camera goes away, close what was seen so far
      // (CAMERA_TURNED_OFF takes over from there when applicable).
      resetSignals();
      video.srcObject = next;
      if (next) {
        paused = false;
        void video.play().catch(() => {
          // Autoplay of a muted, detached element is allowed everywhere this
          // runs; a failure just means no frames, which ticks tolerate.
        });
      } else {
        paused = true;
      }
    };

    const runTick = () => {
      if (disposed || paused) return;
      if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;

      try {
        const timestampMs = performance.now();
        tick += 1;

        if (faceDetector) {
          const faceCount = faceDetector.detectForVideo(video, timestampMs)
            .detections.length;
          noFace.update(faceCount === 0, { faceCount });
          multipleFaces.update(faceCount >= 2, { faceCount });
        }

        if (objectDetector && tick % PHONE_SAMPLE_EVERY_N_TICKS === 0) {
          const detections = objectDetector.detectForVideo(
            video,
            timestampMs,
          ).detections;
          const topScore = detections[0]?.categories[0]?.score ?? 0;
          const active =
            topScore >=
            (phoneStreakActive ? PHONE_SUSTAIN_SCORE : PHONE_ENTER_SCORE);
          phoneStreakActive = active;
          phone.update(active, {
            score: Math.round(topScore * 100) / 100,
          });
        }

        consecutiveFailures = 0;
      } catch {
        consecutiveFailures += 1;
        if (consecutiveFailures >= MAX_CONSECUTIVE_TICK_FAILURES) {
          failoverOrTeardown();
        }
      }
    };

    const closeDetectors = () => {
      const detectors = [faceDetector, objectDetector];
      faceDetector = null;
      objectDetector = null;
      for (const detector of detectors) {
        try {
          detector?.close();
        } catch {
          // A broken GPU detector may throw on close; it's being discarded.
        }
      }
    };

    // Runtime GPU failures (blocklisted driver, lost WebGL context mid-
    // assessment) usually pass creation and only surface inside
    // detectForVideo — so the CPU fallback must live here too, not just at
    // init. Ticks keep running and simply skip while the detectors rebuild.
    const failoverOrTeardown = () => {
      if (delegate === 'CPU' || !createDetectorPair) {
        teardown();
        return;
      }
      delegate = 'CPU';
      consecutiveFailures = 0;
      closeDetectors();
      createDetectorPair('CPU')
        .then(([face, object]) => {
          if (disposed) {
            face.close();
            object.close();
            return;
          }
          faceDetector = face;
          objectDetector = object;
        })
        .catch(() => teardown());
    };

    const teardown = () => {
      disposed = true;
      if (interval) clearInterval(interval);
      if (applyStreamRef.current === applyStream) {
        applyStreamRef.current = null;
      }
      resetSignals();
      video.srcObject = null;
      closeDetectors();
    };

    async function init() {
      try {
        const { FilesetResolver, FaceDetector, ObjectDetector } = await import(
          '@mediapipe/tasks-vision'
        );
        const vision = await FilesetResolver.forVisionTasks(WASM_BASE_URL);
        if (disposed) return;

        // GPU inference where available; wasm CPU otherwise.
        createDetectorPair = async (target: 'GPU' | 'CPU') => {
          const face = await FaceDetector.createFromOptions(vision, {
            baseOptions: { modelAssetPath: FACE_MODEL_URL, delegate: target },
            runningMode: 'VIDEO',
            minDetectionConfidence: MIN_FACE_DETECTION_CONFIDENCE,
          });
          try {
            const object = await ObjectDetector.createFromOptions(vision, {
              baseOptions: {
                modelAssetPath: OBJECT_MODEL_URL,
                delegate: target,
              },
              runningMode: 'VIDEO',
              scoreThreshold: PHONE_DETECTOR_MIN_SCORE,
              categoryAllowlist: ['cell phone'],
              maxResults: PHONE_MAX_RESULTS,
            });
            return [face, object];
          } catch (error) {
            // Don't leak the live detector when its sibling fails (common
            // split: BlazeFace fits the GPU, the heavier EfficientDet
            // doesn't) — a leaked WebGL context can sink the CPU retry.
            face.close();
            throw error;
          }
        };

        try {
          [faceDetector, objectDetector] = await createDetectorPair('GPU');
        } catch {
          delegate = 'CPU';
          [faceDetector, objectDetector] = await createDetectorPair('CPU');
        }
        if (disposed) {
          closeDetectors();
          return;
        }

        applyStreamRef.current = applyStream;
        applyStream(streamRef.current);
        interval = setInterval(runTick, SAMPLE_INTERVAL_MS);
      } catch {
        // Model/wasm download or init failed (offline CDN, blocked by a
        // proxy, unsupported browser) — vision detection is skipped and the
        // assessment continues untouched.
      }
    }

    init();

    return teardown;
  }, [enabled, openEvent, closeEvent, reportEvent]);
}

// Debounces a boolean per-sample signal into open/close integrity events:
// `openAfter` consecutive positive samples open the event, then
// CLOSE_AFTER_NEGATIVE_SAMPLES consecutive negative samples close it. The
// hysteresis delays open/close by a few samples, so both are backdated to
// the start of their streak — the stored range and the `minDurationSeconds`
// cutoff reflect how long the condition really held.
function makeSignalTracker(
  eventType: IntegrityEventType,
  openEvent: OpenIntegrityEvent,
  closeEvent: CloseIntegrityEvent,
  options?: { openAfter?: number; minDurationSeconds?: number },
) {
  const openAfter = options?.openAfter ?? OPEN_AFTER_POSITIVE_SAMPLES;
  const minDurationSeconds =
    options?.minDurationSeconds ?? MIN_EVENT_DURATION_SECONDS;
  let positiveStreak = 0;
  let negativeStreak = 0;
  let positiveStreakStartMs = 0;
  let negativeStreakStartMs = 0;
  let isOpen = false;

  return {
    update(active: boolean, metadata?: Record<string, unknown>) {
      if (active) {
        if (positiveStreak === 0) positiveStreakStartMs = Date.now();
        positiveStreak += 1;
        negativeStreak = 0;
        if (!isOpen && positiveStreak >= openAfter) {
          // openEvent reports whether it registered (the monitoring session
          // may not exist yet); until it does, retry on every positive
          // sample instead of latching a phantom open that would suppress
          // the event for as long as the condition persists.
          isOpen = openEvent(eventType, metadata, {
            startedSecondsAgo: (Date.now() - positiveStreakStartMs) / 1_000,
          });
        }
      } else {
        if (negativeStreak === 0) negativeStreakStartMs = Date.now();
        negativeStreak += 1;
        positiveStreak = 0;
        if (isOpen && negativeStreak >= CLOSE_AFTER_NEGATIVE_SAMPLES) {
          isOpen = false;
          closeEvent(eventType, {
            minDurationSeconds,
            endedSecondsAgo: (Date.now() - negativeStreakStartMs) / 1_000,
          });
        }
      }
    },
    reset() {
      positiveStreak = 0;
      negativeStreak = 0;
      if (isOpen) {
        isOpen = false;
        // The signal held until observation stopped — close at now, no
        // backdate.
        closeEvent(eventType, { minDurationSeconds });
      }
    },
  };
}
